<template>
  <Dialog v-model:open="isOpen">
    <DialogContent
      class="report-builder-dialog-content h-[92vh] max-h-[94vh] w-[95vw] max-w-[1140px] flex flex-col overflow-hidden rounded-[1.5rem] border border-[#d8e4f0] bg-[#f8fbff] p-0 shadow-[0_24px_70px_rgba(15,23,42,0.22)] gap-0"
    >
      <!-- Modal Header (Fixed at top) -->
      <div class="flex-shrink-0 border-b border-[#e3eaf4] px-7 py-3.5 bg-white z-10 flex items-center justify-between gap-4">
        <div>
          <h2 class="text-[1.3rem] font-black tracking-tight text-[#173b5f]">
            {{ mode === "edit" ? "Edit Monthly Report" : "Generate Monthly Report" }}
          </h2>
          <p class="text-xs text-[#64748b] mt-0.5">
            Configure cover branding, metadata, and active sections with an interactive cover page layout.
          </p>
        </div>
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-xl text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition cursor-pointer shrink-0"
          @click="emitClose"
        >
          <X class="h-4 w-4" />
        </button>
      </div>

      <!-- Modal Body Form -->
      <form
        class="flex-1 min-h-0 flex flex-col overflow-hidden"
        @submit.prevent="submit('generate')"
      >
        <!-- Scrollable Content Area -->
        <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-7 py-5 space-y-5 modal-body-scroll">
          <!-- CARD 1: UNIFIED REPORT COVER BUILDER (Matches actual report-cover.blade.php layout) -->
          <div class="rounded-[1.4rem] border border-[#d3e2f2] bg-gradient-to-br from-[#f8fbff] via-[#ffffff] to-[#edf4fc] shadow-sm overflow-hidden">
            <!-- Cover Top Meta Bar: Document Title Badge -->
            <div class="flex items-center justify-between px-6 py-2.5 bg-white/70 border-b border-[#e6eff8] text-[11px]">
              <div class="flex items-center gap-1.5 font-extrabold uppercase tracking-wider text-[10px] text-[#748197]">
                <FileText class="h-3.5 w-3.5 text-[#2855a6]" />
                <span>Interactive Cover Preview</span>
              </div>
              <div class="flex items-center gap-1.5 rounded-full border border-[#d8e4f0] bg-white px-2.5 py-0.5 shadow-2xs">
                <span class="text-[9.5px] font-extrabold uppercase tracking-wider text-[#748197]">Doc Title:</span>
                <input
                  v-model="form.title"
                  type="text"
                  class="bg-transparent text-[11px] font-bold text-[#173b5f] focus:outline-none w-44"
                  placeholder="Monthly Progress Report"
                >
              </div>
            </div>

            <!-- Cover Content Grid -->
            <div class="p-6 md:p-7 grid gap-8 md:grid-cols-[1.12fr_0.88fr] items-start">
              <!-- Left Column of Cover: Logo, Client Title, Accent Bar & Meta Rows -->
              <div class="space-y-3.5 min-w-0 pr-1">
                <!-- 1. Report Logo Uploader Slot (Placed on top above client name) -->
                <div>
                  <input
                    ref="logoInputRef"
                    type="file"
                    class="hidden"
                    accept=".png,.jpg,.jpeg,.webp,.svg"
                    @change="handleLogoFileSelect"
                  >

                  <!-- Logo Box: Filled State (Mirrors exact 1:1 square icon slot on cover) -->
                  <div
                    v-if="hasReportLogo"
                    class="relative w-[88px] h-[88px] rounded-2xl flex items-center justify-center group cursor-pointer border border-transparent hover:border-[#cfe0f2] hover:bg-white/60 p-1.5 transition-all"
                    title="Click canvas to change client logo"
                    @click="triggerLogoUpload"
                  >
                    <img
                      :src="logoPreviewUrl"
                      alt="Client Logo"
                      class="max-h-full max-w-full object-contain select-none pointer-events-none"
                    >
                    <!-- Action Buttons on Top-Right Corner -->
                    <div class="absolute -top-1.5 -right-1.5 flex items-center gap-1 z-10 opacity-80 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        class="h-6 w-6 rounded-full bg-white text-[#173b5f] shadow-md hover:bg-[#f1f5f9] flex items-center justify-center cursor-pointer border border-[#d8e4f0]"
                        title="Change Logo"
                        @click.stop="triggerLogoUpload"
                      >
                        <Upload class="h-3 w-3 text-[#2855a6]" />
                      </button>
                      <button
                        type="button"
                        class="h-6 w-6 rounded-full bg-[#be243d] hover:bg-[#9c182d] text-white shadow-md flex items-center justify-center cursor-pointer"
                        title="Remove Logo"
                        @click.stop="removeLogo"
                      >
                        <Trash2 class="h-3 w-3" />
                      </button>
                    </div>
                  </div>

                  <!-- Logo Box: Empty Upload Prompt (Exact same 1:1 square frame) -->
                  <div
                    v-else
                    class="w-[88px] h-[88px] rounded-2xl border-2 border-dashed border-[#c1cfe1] hover:border-[#2855a6] bg-white/40 hover:bg-white/80 p-2 flex flex-col items-center justify-center gap-1 transition cursor-pointer group shadow-2xs"
                    title="Click to select client logo"
                    @click="triggerLogoUpload"
                  >
                    <div class="h-8 w-8 rounded-full bg-[#eaf3fb] text-[#2855a6] flex items-center justify-center group-hover:scale-105 transition-transform shrink-0">
                      <Upload class="h-4 w-4" />
                    </div>
                    <div class="text-center select-none pointer-events-none">
                      <div class="text-[10px] font-black text-[#173b5f]">Logo</div>
                    </div>
                  </div>

                  <p
                    v-if="errors['logo']"
                    class="text-[11px] text-[#be243d] font-bold mt-1"
                  >
                    {{ errors['logo'] }}
                  </p>
                </div>

                <!-- 2. Client Name Heading or Dropdown -->
                <div class="pt-1">
                  <!-- When opened for a specific client: Show prominent company name -->
                  <div
                    v-if="isClientLocked"
                    class="space-y-0.5"
                  >
                    <h1 class="text-[2rem] font-black text-[#062d5f] uppercase tracking-tight leading-[1.02] break-words">
                      {{ preparedFor }}
                    </h1>
                  </div>

                  <!-- When opened globally: Show client selection dropdown -->
                  <div
                    v-else
                    class="space-y-1"
                  >
                    <label class="text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
                      Client Account
                    </label>
                    <select
                      v-model="form.client_id"
                      class="field-input font-black text-[#062d5f]"
                    >
                      <option
                        v-for="client in clients"
                        :key="client.id"
                        :value="client.client_id"
                      >
                        {{ client.company_name }} ({{ client.client_id }})
                      </option>
                    </select>
                    <p
                      v-if="errors['client_id']"
                      class="text-xs text-[#be243d] font-bold mt-1"
                    >
                      {{ errors['client_id'] }}
                    </p>
                  </div>
                </div>

                <!-- 3. Signature 4-Color Gradient Accent Line (Directly below company name) -->
                <div
                  class="h-[4px] w-52 rounded-full my-2.5"
                  style="background: linear-gradient(90deg, #0b3f75 0 24%, #e5192d 24% 49%, #f5a300 49% 74%, #17a66b 74% 100%);"
                />

                <!-- 4. Subtitle -->
                <p class="text-[13px] font-medium leading-snug text-[#5f6672]">
                  Digital Marketing Partner Monthly Performance Update
                </p>

                <!-- 5. Metadata Rows -->
                <div class="space-y-0 border-t border-[#e2edf8]">
                  <!-- Meta Row 1: Report Month -->
                  <div class="flex items-start gap-3 py-3 border-b border-[#e9f1fa]">
                    <div class="h-9 w-9 rounded-full bg-[#e5192d] text-white flex items-center justify-center shrink-0 shadow-2xs mt-0.5">
                      <Calendar class="h-4 w-4" />
                    </div>
                    <div class="min-w-0 flex-1 space-y-2">
                      <div class="text-[11px] font-extrabold text-[#0b3f75] uppercase tracking-[0.07em]">
                        Report Month
                      </div>
                      <div class="flex flex-wrap items-center gap-2">
                        <!-- Inline Month Selector -->
                        <select
                          v-model.number="form.month_number"
                          class="field-input w-auto min-w-[110px] font-bold cursor-pointer"
                        >
                          <option
                            v-for="month in monthOptions"
                            :key="month"
                            :value="month"
                          >
                            Month {{ month }}
                          </option>
                        </select>
                        <!-- Month Label Input -->
                        <input
                          v-model="form.report_month_label"
                          class="field-input flex-1 min-w-[150px]"
                          type="text"
                          placeholder="e.g. May – June 2026"
                        >
                      </div>
                      <!-- Date Range -->
                      <div class="flex items-center gap-2">
                        <span class="text-[11px] font-extrabold text-[#748197] uppercase tracking-[0.07em] shrink-0">Range:</span>
                        <input
                          v-model="form.date_from"
                          type="date"
                          class="field-input w-auto"
                        >
                        <span class="text-[13px] text-[#94a3b8] font-medium shrink-0">to</span>
                        <input
                          v-model="form.date_to"
                          type="date"
                          :min="form.date_from || undefined"
                          class="field-input w-auto"
                        >
                      </div>
                    </div>
                  </div>

                  <!-- Meta Row 2: Prepared By -->
                  <div class="flex items-center gap-3 py-3 border-b border-[#e9f1fa]">
                    <div class="h-9 w-9 rounded-full bg-[#17a66b] text-white flex items-center justify-center shrink-0 shadow-2xs">
                      <TrendingUp class="h-4 w-4" />
                    </div>
                    <div class="min-w-0 flex-1 space-y-2">
                      <div class="text-[11px] font-extrabold text-[#0b3f75] uppercase tracking-[0.07em]">
                        Prepared By
                      </div>
                      <input
                        v-model="preparedBy"
                        class="field-input"
                        type="text"
                        placeholder="Proweaver"
                      >
                    </div>
                  </div>

                  <!-- Meta Row 3: Supporting Statement -->
                  <div class="flex items-start gap-3 py-3">
                    <div class="h-9 w-9 rounded-full bg-[#062d5f] text-white flex items-center justify-center shrink-0 shadow-2xs mt-0.5">
                      <Check class="h-4 w-4 stroke-[3]" />
                    </div>
                    <div class="text-sm text-[#475569] font-medium leading-relaxed pt-1.5">
                      Supporting stronger visibility, inquiry pathways, and lead tracking across digital channels.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column of Cover: Hero Banner Image Framed Card (~4:5 ratio) -->
              <div class="space-y-2 flex flex-col items-center">
                <div class="flex items-center justify-between w-full">
                  <label class="text-[0.68rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
                    Cover Hero Banner (1:1 Frame)
                  </label>
                  <span
                    v-if="hasHeroImage"
                    class="text-[10px] font-bold text-[#16a34a] flex items-center gap-1"
                  >
                    <CheckCircle2 class="h-3 w-3" /> Framed
                  </span>
                </div>

                <!-- Framed Cropper Viewport (Mirrors the rounded template slot) -->
                <div class="w-full rounded-[2rem] overflow-hidden border border-[#cfe0f2] shadow-[0_16px_36px_rgba(4,36,91,0.12)] p-2.5 bg-gradient-to-br from-[#e6f0fa] to-white flex flex-col items-center justify-center">
                  <InlineImageCanvasCropper
                    ref="bannerCropperRef"
                    :model-value="branding.heroImageFile"
                    :existing-url="branding.heroImageUrl"
                    :aspect-ratio="1"
                    :is-banner="true"
                    label="Click to Select Hero Banner"
                    hint="PNG, JPG, WEBP • Drag to pan & Ctrl+Scroll to zoom"
                    accept=".png,.jpg,.jpeg,.webp"
                    :max-size-m-b="10"
                    :target-width="1200"
                    :target-height="1200"
                    file-name="hero-banner.png"
                    @update:model-value="handleHeroChange"
                  />
                </div>

                <p
                  v-if="errors['hero_image']"
                  class="text-[11px] text-[#be243d] font-bold mt-1 text-center"
                >
                  {{ errors['hero_image'] }}
                </p>
              </div>
            </div>

          </div>

          <!-- CARD 2: SECTION MODULES (with AI Extraction Model in the Toolbar) -->
          <div class="rounded-[1.25rem] border border-[#d8e4f0] bg-white p-5 shadow-2xs space-y-4">
            <!-- Section Modules Header Toolbar -->
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#f0f4fa] pb-3">
              <div>
                <h3 class="text-[0.94rem] font-black text-[#173b5f]">
                  Section Modules
                </h3>
                <p class="text-[0.88rem] text-[#64748b] mt-0.5">
                  Select which modules to generate and attach corresponding raw data files.
                </p>
              </div>

              <!-- AI Model Selector & Select/Clear Buttons -->
              <div class="flex flex-wrap items-center gap-2.5">
                <!-- AI Extraction Model Dropdown Badge -->
                <div class="flex items-center gap-1.5 rounded-full border border-[#bfdbfe] bg-[#eff6ff] px-3 py-1 shadow-2xs">
                  <Sparkles class="h-3.5 w-3.5 text-[#2563eb]" />
                  <span class="text-[11px] font-extrabold text-[#1e3a5f]">AI Model:</span>
                  <select
                    v-model="form.ai_model"
                    class="bg-transparent text-xs font-bold text-[#2563eb] focus:outline-none cursor-pointer"
                    :disabled="modelsLoading"
                  >
                    <optgroup
                      v-if="openAiModels.length"
                      label="OpenAI"
                    >
                      <option
                        v-for="m in openAiModels"
                        :key="m.name"
                        :value="m.name"
                      >
                        {{ m.label }}
                      </option>
                    </optgroup>
                    <optgroup
                      v-if="ollamaModels.length"
                      label="Local (Ollama)"
                    >
                      <option
                        v-for="m in ollamaModels"
                        :key="m.name"
                        :value="m.name"
                      >
                        {{ m.label }}
                      </option>
                    </optgroup>
                  </select>
                </div>

                <!-- Select All / Clear All Buttons -->
                <button
                  type="button"
                  class="text-xs font-bold text-[#2855a6] hover:text-[#173b5f] hover:underline cursor-pointer"
                  @click="selectAllSections"
                >
                  Select All
                </button>
                <span class="text-[#cbd5e1]">•</span>
                <button
                  type="button"
                  class="text-xs font-bold text-[#64748b] hover:text-[#dc2626] cursor-pointer"
                  @click="deselectAllSections"
                >
                  Clear All
                </button>
              </div>
            </div>

            <div class="space-y-2.5">
              <!-- Executive Snapshot (Required - Locked) -->
              <div class="rounded-[0.95rem] border border-[#d8e4f0] bg-[#f8fbff] p-3 space-y-2">
                <div class="flex items-center justify-between gap-3">
                  <div class="flex items-center gap-2.5 min-w-0">
                    <div class="h-8 w-8 rounded-lg bg-[#eaf3fb] text-[#2855a6] flex items-center justify-center shrink-0">
                      <BarChart3 class="h-4 w-4" />
                    </div>
                    <div class="min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="text-[0.88rem] font-black text-[#173b5f]">Executive Snapshot</span>
                        <span class="pill pill-blue">Required</span>
                      </div>
                      <p class="text-[0.82rem] text-[#64748b] truncate">
                        Summarizes metric cards and overall progress.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center gap-1.5 opacity-80 cursor-not-allowed">
                    <input
                      type="checkbox"
                      checked
                      disabled
                      class="h-4 w-4 rounded border-[#c1cfe1] text-[#2855a6]"
                    >
                    <Lock class="h-3 w-3 text-[#94a3b8]" />
                  </div>
                </div>
              </div>

              <!-- Configurable Dynamic Sections from config -->
              <template
                v-for="section in reportSectionConfig"
                :key="section.key"
              >
                <div
                  v-if="isVisibleSection(section)"
                  class="rounded-[0.95rem] border transition-all p-3 space-y-2"
                  :class="sections[section.key].enabled ? 'border-[#2855a6] bg-[#fdfeff] shadow-2xs' : 'border-[#e2eaf4] bg-white opacity-85 hover:opacity-100'"
                >
                  <div class="flex items-center justify-between gap-3">
                    <div class="flex items-center gap-2.5 min-w-0">
                      <div
                        class="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 transition-colors"
                        :class="sections[section.key].enabled ? 'bg-[#eaf3fb] text-[#2855a6]' : 'bg-slate-100 text-slate-500'"
                      >
                        <component
                          :is="getSectionIcon(section.key)"
                          class="h-4 w-4"
                        />
                      </div>
                      <div class="min-w-0">
                        <div class="flex items-center gap-2">
                          <span class="text-[0.88rem] font-black text-[#173b5f]">{{ section.label }}</span>
                          <span
                            class="pill"
                            :class="section.requiresFiles ? 'pill-blue' : 'pill-yellow'"
                          >
                            Optional
                          </span>
                        </div>
                        <p class="text-[0.82rem] text-[#64748b] truncate">
                          {{ section.helper }}
                        </p>
                      </div>
                    </div>

                    <!-- Toggle Switch -->
                    <div class="flex items-center gap-2 shrink-0">
                      <label class="relative inline-flex items-center cursor-pointer">
                        <input
                          v-model="sections[section.key].enabled"
                          type="checkbox"
                          class="sr-only peer"
                          @change="onSectionToggle(section.key)"
                        >
                        <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#2855a6]" />
                      </label>
                    </div>
                  </div>

                  <!-- File Upload Row (only when section is enabled) -->
                  <div
                    v-if="sections[section.key].enabled"
                    class="pt-1.5 border-t border-[#f0f4fa]"
                  >
                    <!-- Hidden file input for this section -->
                    <input
                      :ref="(el) => { if (el) sectionFileInputRefs[section.key] = el as HTMLInputElement }"
                      type="file"
                      class="hidden"
                      multiple
                      @change="handleSectionFileSelect(section.key, $event)"
                    >

                    <!-- Existing & New Files Chips -->
                    <div
                      v-if="getAvailableSectionFiles(sections[section.key]).length > 0"
                      class="space-y-1.5"
                    >
                      <div class="flex flex-wrap items-center gap-2">
                        <!-- Existing files -->
                        <div
                          v-for="file in sections[section.key].existingFiles.filter(f => !sections[section.key].removedFileIds.includes(f.id!))"
                          :key="file.id"
                          class="inline-flex items-center gap-2 rounded-lg border border-[#c5d8ea] bg-[#f0f6fc] px-2.5 py-1 text-xs text-[#1e3a5f]"
                        >
                          <FileText class="h-3.5 w-3.5 text-[#2855a6]" />
                          <span class="font-bold max-w-[180px] truncate">{{ file.name }}</span>
                          <span class="pill pill-green text-[9px] py-0 px-1.5">Uploaded</span>
                          <button
                            type="button"
                            class="text-[#dc2626] hover:text-[#b91c1c] text-[11px] font-bold ml-1 cursor-pointer"
                            title="Remove file"
                            @click="removeSectionExistingFile(section.key, file.id!)"
                          >
                            Remove
                          </button>
                        </div>

                        <!-- Newly selected files -->
                        <div
                          v-for="(file, idx) in sections[section.key].newFiles"
                          :key="idx"
                          class="inline-flex items-center gap-2 rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] px-2.5 py-1 text-xs text-[#166534]"
                        >
                          <FileText class="h-3.5 w-3.5 text-[#16a34a]" />
                          <span class="font-bold max-w-[180px] truncate">{{ file.name }}</span>
                          <span class="pill pill-green text-[9px] py-0 px-1.5">Ready</span>
                          <button
                            type="button"
                            class="text-[#dc2626] hover:text-[#b91c1c] text-[11px] font-bold ml-1 cursor-pointer"
                            title="Remove file"
                            @click="removeSectionNewFile(section.key, idx)"
                          >
                            Remove
                          </button>
                        </div>

                        <!-- Add more files button -->
                        <button
                          type="button"
                          class="inline-flex items-center gap-1 text-[11px] font-bold text-[#2855a6] hover:underline cursor-pointer ml-1"
                          @click="triggerSectionFileUpload(section.key)"
                        >
                          <Plus class="h-3 w-3" />
                          <span>Add file</span>
                        </button>
                      </div>
                    </div>

                    <!-- Empty files upload prompt -->
                    <div
                      v-else
                      class="flex items-center gap-3"
                    >
                      <button
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-lg border border-[#d7e2ef] bg-[#f6f9ff] hover:bg-[#eaf3fb] hover:border-[#bfdbfe] px-3 py-1.5 text-xs font-bold text-[#2855a6] transition cursor-pointer shadow-2xs"
                        @click="triggerSectionFileUpload(section.key)"
                      >
                        <Upload class="h-3.5 w-3.5" />
                        <span>Upload file</span>
                      </button>
                      <span class="text-[11px] text-[#748197]">
                        Upload the source file for this section.
                      </span>
                    </div>

                    <!-- Error notice for section -->
                    <p
                      v-if="errors[`sections.${section.key}.files`]"
                      class="text-[11px] text-[#be243d] font-bold mt-1.5"
                    >
                      {{ errors[`sections.${section.key}.files`] }}
                    </p>
                  </div>
                </div>
              </template>

              <!-- Monthly Recommendations (Required - Locked) -->
              <div class="rounded-[0.95rem] border border-[#d8e4f0] bg-[#f8fbff] p-3 space-y-2">
                <div class="flex items-center justify-between gap-3">
                  <div class="flex items-center gap-2.5 min-w-0">
                    <div class="h-8 w-8 rounded-lg bg-[#eaf3fb] text-[#2855a6] flex items-center justify-center shrink-0">
                      <Lightbulb class="h-4 w-4" />
                    </div>
                    <div class="min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="text-[0.88rem] font-black text-[#173b5f]">Monthly Recommendations</span>
                        <span class="pill pill-blue">Required</span>
                      </div>
                      <p class="text-[0.82rem] text-[#64748b] truncate">
                        Includes strategic roadmap and general recommendations.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center gap-1.5 opacity-80 cursor-not-allowed">
                    <input
                      type="checkbox"
                      checked
                      disabled
                      class="h-4 w-4 rounded border-[#c1cfe1] text-[#2855a6]"
                    >
                    <Lock class="h-3 w-3 text-[#94a3b8]" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- CARD 3: COMPACT READINESS STATUS BAR (Above Footer) -->
          <div class="rounded-[1rem] border border-[#d8e4f0] bg-white p-3.5 shadow-2xs flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap items-center gap-4 text-xs">
              <!-- Logo Check -->
              <div class="flex items-center gap-1.5">
                <CheckCircle2
                  v-if="hasReportLogo"
                  class="h-4 w-4 text-[#16a34a] shrink-0"
                />
                <AlertCircle
                  v-else
                  class="h-4 w-4 text-[#ea580c] shrink-0"
                />
                <span
                  class="font-bold"
                  :class="hasReportLogo ? 'text-[#1e293b]' : 'text-[#ea580c]'"
                >
                  {{ hasReportLogo ? 'Logo ready' : 'Logo missing (required)' }}
                </span>
              </div>

              <!-- Hero Banner Check -->
              <div class="flex items-center gap-1.5">
                <CheckCircle2
                  v-if="hasHeroImage"
                  class="h-4 w-4 text-[#16a34a] shrink-0"
                />
                <AlertCircle
                  v-else
                  class="h-4 w-4 text-[#ea580c] shrink-0"
                />
                <span
                  class="font-bold"
                  :class="hasHeroImage ? 'text-[#1e293b]' : 'text-[#ea580c]'"
                >
                  {{ hasHeroImage ? 'Hero banner framed' : 'Hero banner missing (required)' }}
                </span>
              </div>

              <!-- Sections Count Check -->
              <div class="flex items-center gap-1.5">
                <CheckCircle2
                  v-if="enabledSectionsCount > 0 && missingSectionFiles.length === 0"
                  class="h-4 w-4 text-[#16a34a] shrink-0"
                />
                <AlertCircle
                  v-else
                  class="h-4 w-4 text-[#ea580c] shrink-0"
                />
                <span class="font-bold text-[#1e293b]">
                  {{ enabledSectionsCount }} section{{ enabledSectionsCount === 1 ? '' : 's' }} enabled
                </span>
              </div>
            </div>

            <div
              class="text-[11px] font-black px-3 py-1 rounded-full"
              :class="isReportFormValid ? 'bg-[#ecfdf5] text-[#15803d]' : 'bg-[#fff7ed] text-[#c2410c]'"
            >
              {{ isReportFormValid ? '✓ Ready to Generate' : 'Action Required' }}
            </div>
          </div>

          <!-- Global Error Notification -->
          <div
            v-if="error"
            class="rounded-[1rem] border border-[#f5c2c7] bg-[#fff5f5] px-4 py-3 text-sm text-[#b42318] font-bold"
          >
            {{ error }}
          </div>
        </div>

        <!-- Dialog Footer Actions (Fixed at bottom) -->
        <DialogFooter class="flex-shrink-0 border-t border-[#e3eaf4] px-7 py-3.5 bg-white z-10 flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            class="rounded-[0.95rem] border-[#d7e2ef] bg-white px-5 py-2 font-bold text-[#2855a6] hover:bg-[#f6f9ff] cursor-pointer"
            @click="emitClose"
          >
            Cancel
          </Button>

          <div class="flex items-center gap-2.5">
            <Button
              type="button"
              class="rounded-[0.95rem] bg-[#6b7a90] hover:bg-[#59667a] px-5 py-2 font-bold text-white cursor-pointer"
              :disabled="loading"
              @click="submit('draft')"
            >
              Save Draft
            </Button>
            <Button
              type="button"
              class="rounded-[0.95rem] px-6 py-2 font-black text-white transition-all shadow-sm cursor-pointer"
              :class="isReportFormValid && !loading ? 'bg-[#173b5f] hover:bg-[#0f2742]' : 'bg-[#94a3b8] cursor-not-allowed'"
              :disabled="!isReportFormValid || loading"
              @click="submit('generate')"
            >
              Generate Report
            </Button>
          </div>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import http from "@/services/http";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import type {
  GenerateMonthlyReportForm,
  ReportClientCard,
  ReportRecord,
} from "@/types/reports";
import {
  reportSectionConfig,
  type ReportSectionKey,
  type ReportSectionConfigItem,
  type ReportSectionFile,
  type ExtractedFileContent,
} from "./reportSectionConfig";
import InlineImageCanvasCropper from "./InlineImageCanvasCropper.vue";
import {
  X,
  Lock,
  Upload,
  Plus,
  FileText,
  BarChart3,
  Lightbulb,
  Share2,
  Bot,
  Podcast,
  Search,
  Megaphone,
  Users,
  Layers,
  Compass,
  CheckCircle2,
  AlertCircle,
  Image as ImageIcon,
  Trash2,
  Calendar,
  TrendingUp,
  Sparkles,
  Check,
} from "lucide-vue-next";

const props = defineProps<{
  open: boolean;
  loading?: boolean;
  error?: string | null;
  clients: ReportClientCard[];
  selectedClient?: ReportClientCard | null;
  report?: ReportRecord | null;
  selectedMonthNumber?: number | null;
  mode?: "create" | "edit";
}>();

const emit = defineEmits<{
  (event: "update:open", value: boolean): void;
  (
    event: "generate",
    value: {
      payload: GenerateMonthlyReportForm;
      files: Record<string, File[]>;
      action: "draft" | "html" | "pdf" | "generate";
      reportId?: number | null;
      extractedPayload?: Record<string, string>;
    }
  ): void;
}>();

const isOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit("update:open", value),
});

// ─── AI Model list ─────────────────────────────────────────────────────────────

interface AiModel {
  name: string;
  provider: "openai" | "ollama";
  label: string;
}

const modelsLoading = ref(false);
const availableModels = ref<AiModel[]>([
  { name: "gpt-4o-mini", provider: "openai", label: "GPT-4o Mini (Default)" },
  { name: "gpt-4o",      provider: "openai", label: "GPT-4o (High Reasoning)" },
  { name: "gpt-4.1",      provider: "openai", label: "GPT-4.1 (Latest High Quality)" },
  { name: "gpt-4.1-mini", provider: "openai", label: "GPT-4.1 Mini (Fast & Capable)" },
  { name: "gpt-4.1-nano", provider: "openai", label: "GPT-4.1 Nano (Fastest & Cheapest)" },
  { name: "gpt-5",         provider: "openai", label: "GPT-5 (Flagship Reasoning)" },
  { name: "gpt-5-mini",    provider: "openai", label: "GPT-5 Mini (Fast Reasoning)" },
  { name: "gpt-5-nano",    provider: "openai", label: "GPT-5 Nano (Fastest)" },
]);

const openAiModels = computed(() =>
  availableModels.value.filter((m) => m.provider === "openai")
);
const ollamaModels = computed(() =>
  availableModels.value.filter((m) => m.provider === "ollama")
);

async function fetchAvailableModels() {
  modelsLoading.value = true;
  try {
    const { data } = await http.get("/api/ai/models");
    if (Array.isArray(data.models) && data.models.length > 0) {
      availableModels.value = data.models;
    }
  } catch {
    // Keep the hardcoded defaults on error
  } finally {
    modelsLoading.value = false;
  }
}

// ─── Form state ────────────────────────────────────────────────────────────────

const form = reactive({
  client_id: "",
  month_number: 1,
  report_month_label: "",
  title: "Monthly Progress Report",
  date_from: "",
  date_to: "",
  selected_sections: ["cover", "executive_snapshot", "recommendation"],
  status: "generated" as GenerateMonthlyReportForm["status"],
  publish: false,
  ai_model: "gpt-4o-mini",
});

const branding = reactive({
  logoFile: null as File | null,
  logoUrl: "",
  heroImageFile: null as File | null,
  heroImageUrl: "",
});

const logoInputRef = ref<HTMLInputElement | null>(null);
const bannerCropperRef = ref<InstanceType<typeof InlineImageCanvasCropper> | null>(null);

const executiveSnapshot = reactive({
  content: "",
});

const overallMonthlyRecommendation = reactive({
  content: "",
});

const sections = reactive<Record<ReportSectionKey, {
  enabled: boolean;
  existingFiles: ReportSectionFile[];
  newFiles: File[];
  removedFileIds: (number | string)[];
  insight: string;
  recommendation: string;
}>>({
  journey: { enabled: false, existingFiles: [], newFiles: [], removedFileIds: [], insight: "", recommendation: "" },
  socialMediaPerformance: { enabled: false, existingFiles: [], newFiles: [], removedFileIds: [], insight: "", recommendation: "" },
  aiChatbotManagement: { enabled: false, existingFiles: [], newFiles: [], removedFileIds: [], insight: "", recommendation: "" },
  aiPodcastProgress: { enabled: false, existingFiles: [], newFiles: [], removedFileIds: [], insight: "", recommendation: "" },
  blogManagement: { enabled: false, existingFiles: [], newFiles: [], removedFileIds: [], insight: "", recommendation: "" },
  seoManagement: { enabled: false, existingFiles: [], newFiles: [], removedFileIds: [], insight: "", recommendation: "" },
  adsManagement: { enabled: false, existingFiles: [], newFiles: [], removedFileIds: [], insight: "", recommendation: "" },
  generatedLeads: { enabled: false, existingFiles: [], newFiles: [], removedFileIds: [], insight: "", recommendation: "" },
  promotionalMaterials: { enabled: false, existingFiles: [], newFiles: [], removedFileIds: [], insight: "", recommendation: "" },
});

const sectionExtractedContents = reactive<
  Record<ReportSectionKey, ExtractedFileContent[]>
>({
  journey: [],
  socialMediaPerformance: [],
  aiChatbotManagement: [],
  aiPodcastProgress: [],
  blogManagement: [],
  seoManagement: [],
  adsManagement: [],
  generatedLeads: [],
  promotionalMaterials: [],
});

const sectionFileInputRefs = ref<Record<string, HTMLInputElement | null>>({});
const errors = ref<Record<string, string>>({});
const preparedBy = ref("Proweaver");

// ─── Computeds ─────────────────────────────────────────────────────────────────

const preparedFor = computed(() => {
  return selectedClientRecord.value?.company_name || "Client";
});

const selectedClientRecord = computed(() => {
  return (
    props.clients.find((client) => client.client_id === form.client_id) ||
    null
  );
});

const isClientLocked = computed(() => !!props.selectedClient);

const monthOptions = computed(() =>
  Array.from({ length: 12 }, (_, index) => index + 1)
);

function isVisibleSection(sec: ReportSectionConfigItem): boolean {
  if (sec.key === "journey") {
    return Number(form.month_number) === 3;
  }
  return true;
}

const enabledSectionsCount = computed(() => {
  let count = 0;
  reportSectionConfig.forEach((sec) => {
    if (isVisibleSection(sec) && sections[sec.key]?.enabled) count++;
  });
  return count;
});

function getSectionIcon(key: ReportSectionKey) {
  switch (key) {
    case "journey": return Compass;
    case "socialMediaPerformance": return Share2;
    case "aiChatbotManagement": return Bot;
    case "aiPodcastProgress": return Podcast;
    case "blogManagement": return FileText;
    case "seoManagement": return Search;
    case "adsManagement": return Megaphone;
    case "generatedLeads": return Users;
    case "promotionalMaterials": return Layers;
    default: return FileText;
  }
}

// ─── Logo & Hero Handlers ──────────────────────────────────────────────────────

const hasReportLogo = computed(() => Boolean(branding.logoFile || branding.logoUrl));
const hasHeroImage = computed(() => Boolean(branding.heroImageFile || branding.heroImageUrl));

const logoPreviewUrl = computed(() => {
  if (branding.logoFile) {
    return URL.createObjectURL(branding.logoFile);
  }
  return branding.logoUrl || "";
});

function triggerLogoUpload() {
  logoInputRef.value?.click();
}

function handleLogoFileSelect(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files?.[0]) {
    branding.logoFile = target.files[0];
    delete errors.value["logo"];
    target.value = "";
  }
}

function removeLogo() {
  branding.logoFile = null;
  branding.logoUrl = "";
}

function triggerHeroUpload() {
  bannerCropperRef.value?.triggerUpload();
}

function handleHeroChange(file: File | null) {
  branding.heroImageFile = file;
  if (file) {
    delete errors.value["hero_image"];
  }
}

// ─── File helpers ──────────────────────────────────────────────────────────────

function getAvailableSectionFiles(
  section: { existingFiles: ReportSectionFile[]; newFiles: File[]; removedFileIds: (number | string)[] }
): (ReportSectionFile | File)[] {
  const retainedExisting = (section.existingFiles ?? []).filter(
    (f) => !(section.removedFileIds ?? []).includes(f.id!)
  );
  return [...retainedExisting, ...(section.newFiles ?? [])];
}

function triggerSectionFileUpload(key: ReportSectionKey) {
  sectionFileInputRefs.value[key]?.click();
}

function handleSectionFileSelect(key: ReportSectionKey, e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    const files = Array.from(target.files);
    sections[key].newFiles = [...sections[key].newFiles, ...files];
    clearSectionErrors(key);
    target.value = "";
  }
}

function removeSectionNewFile(key: ReportSectionKey, index: number) {
  sections[key].newFiles.splice(index, 1);
}

function removeSectionExistingFile(key: ReportSectionKey, fileId: number | string) {
  if (!sections[key].removedFileIds.includes(fileId)) {
    sections[key].removedFileIds.push(fileId);
  }
}

// ─── Per-section validity ─────────────────────────────────────────────────────

function isSectionValid(sectionKey: ReportSectionKey): boolean {
  const config = reportSectionConfig.find((c) => c.key === sectionKey);
  const section = sections[sectionKey];

  if (!config || !section) return false;
  if (!isVisibleSection(config)) return true;
  if (!section.enabled) return true;

  if (config.requiresFiles && getAvailableSectionFiles(section).length === 0) {
    return false;
  }
  return true;
}

const missingSectionFiles = computed(() => {
  const missing: string[] = [];
  reportSectionConfig.forEach((sec) => {
    if (isVisibleSection(sec) && sections[sec.key]?.enabled && sec.requiresFiles) {
      if (getAvailableSectionFiles(sections[sec.key]).length === 0) {
        missing.push(sec.label);
      }
    }
  });
  return missing;
});

const enabledSectionsAreValid = computed(() => {
  return reportSectionConfig.every((config) => isSectionValid(config.key));
});

const isReportFormValid = computed(() => {
  const requiredFieldsValid =
    Boolean(form.client_id) &&
    Boolean(form.title?.trim()) &&
    Boolean(form.report_month_label?.trim());

  return (
    requiredFieldsValid &&
    hasReportLogo.value &&
    hasHeroImage.value &&
    enabledSectionsAreValid.value
  );
});

// ─── Error helpers ─────────────────────────────────────────────────────

function clearSectionErrors(key: ReportSectionKey) {
  delete errors.value[`sections.${key}.files`];
  delete errors.value[`sections.${key}.insight`];
  delete errors.value[`sections.${key}.recommendation`];
}

function onSectionToggle(key: ReportSectionKey) {
  const state = sections[key];
  if (!state.enabled) {
    state.newFiles.forEach((file) => {
      const objUrl = (file as any).previewUrl;
      if (objUrl) URL.revokeObjectURL(objUrl);
    });
    state.newFiles = [];
    state.removedFileIds = [];
    sectionExtractedContents[key] = [];
    clearSectionErrors(key);
  }
}

function selectAllSections() {
  reportSectionConfig.forEach((sec) => {
    if (isVisibleSection(sec)) {
      sections[sec.key].enabled = true;
    }
  });
}

function deselectAllSections() {
  reportSectionConfig.forEach((sec) => {
    if (isVisibleSection(sec)) {
      sections[sec.key].enabled = false;
      onSectionToggle(sec.key);
    }
  });
}

// ─── Defaults & Form Reset ─────────────────────────────────────────────────────

function setDefaults() {
  form.client_id =
    props.selectedClient?.client_id ||
    props.report?.client_id ||
    props.clients[0]?.client_id ||
    "";

  form.month_number =
    props.selectedMonthNumber ||
    props.report?.month_number ||
    1;

  form.report_month_label =
    props.report?.report_month_label ||
    `Month ${form.month_number} Report`;

  form.title = props.report?.title || "Monthly Progress Report";
  form.date_from = props.report?.date_from || "";
  form.date_to = props.report?.date_to || "";
  form.publish = false;
  form.status = "generated";
  form.ai_model = "gpt-4o-mini";

  preparedBy.value =
    (props.report?.report_data as Record<string, any>)?.prepared_by ||
    "Proweaver";

  branding.logoFile = null;
  branding.logoUrl = "";
  branding.heroImageFile = null;
  branding.heroImageUrl = "";

  errors.value = {};

  reportSectionConfig.forEach((sec) => {
    sections[sec.key] = {
      enabled: false,
      existingFiles: [],
      newFiles: [],
      removedFileIds: [],
      insight: "",
      recommendation: "",
    };
  });

  if (props.report) {
    const reportData = (props.report.report_data || {}) as Record<string, any>;
    form.ai_model = reportData.ai_model || "gpt-4o-mini";
    executiveSnapshot.content = reportData.executive_snapshot_note || reportData.executive_note || "";
    overallMonthlyRecommendation.content = reportData.overall_recommendation_summary || reportData.recommendation_summary || "";

    const logoAsset = props.report.assets?.find(
      (a) => a.section_key === "report_logo" || a.section_key === "logo"
    );
    branding.logoUrl = logoAsset ? logoAsset.url : "";

    const heroAsset = props.report.assets?.find(
      (a) => a.section_key === "hero_banner" || a.section_key === "hero_image"
    );
    branding.heroImageUrl = heroAsset ? heroAsset.url : "";

    reportSectionConfig.forEach((sec) => {
      const isEnabled = props.report?.selected_sections?.includes(sec.key);
      const secData = reportData.sections?.[sec.key] || {};

      const dbKeys = [sec.dbKey, sec.key];
      const existingFiles = (props.report?.assets || [])
        .filter((asset) => dbKeys.includes(asset.section_key || ""))
        .map((asset) => ({
          id: asset.id,
          name: asset.original_name,
          size: asset.size,
          mimeType: asset.mime_type,
          url: asset.url,
          source: "server" as const,
        }));

      sections[sec.key] = {
        enabled: !!isEnabled,
        existingFiles,
        newFiles: [],
        removedFileIds: [],
        insight: secData.insight || "",
        recommendation: secData.recommendation || "",
      };
    });
  } else {
    form.selected_sections = ["cover", "executive_snapshot", "recommendation"];
  }
}

function validateReportForm(): boolean {
  errors.value = {};
  let isValid = true;

  if (!form.client_id) {
    errors.value["client_id"] = "Client is required";
    isValid = false;
  }
  if (!form.title?.trim()) {
    errors.value["title"] = "Report Title is required";
    isValid = false;
  }
  if (!form.report_month_label?.trim()) {
    errors.value["report_month_label"] = "Report Month Label is required";
    isValid = false;
  }

  if (!branding.logoFile && !branding.logoUrl) {
    errors.value["logo"] = "Report Logo is required.";
    isValid = false;
  }
  if (!branding.heroImageFile && !branding.heroImageUrl) {
    errors.value["hero_image"] = "Hero Banner Image is required.";
    isValid = false;
  }

  reportSectionConfig.forEach((sec) => {
    const state = sections[sec.key];
    if (!state.enabled) return;

    if (sec.requiresFiles && getAvailableSectionFiles(state).length === 0) {
      errors.value[`sections.${sec.key}.files`] =
        "At least one supporting file is required.";
      isValid = false;
    }
  });

  return isValid;
}

function serializeReportPayload() {
  const selected: string[] = ["cover", "executive_snapshot", "recommendation"];
  const payloadSections: Record<string, { enabled: boolean; insight?: string; recommendation?: string }> = {};

  reportSectionConfig.forEach((sec) => {
    const s = sections[sec.key];
    if (isVisibleSection(sec) && s.enabled) {
      selected.push(sec.key);
      payloadSections[sec.key] = {
        enabled: true,
        insight: s.insight,
        recommendation: s.recommendation?.trim() ? s.recommendation : undefined,
      };
    } else {
      payloadSections[sec.key] = { enabled: false };
    }
  });

  const reportData = {
    prepared_by: preparedBy.value,
    generated_by: "Current User",
    template: "Proweaver Monthly Progress Report",
    executive_snapshot_note: executiveSnapshot.content,
    overall_recommendation_summary: overallMonthlyRecommendation.content,
    sections: payloadSections,
    ai_model: form.ai_model,
  };

  let allRemovedFileIds: (number | string)[] = [];
  reportSectionConfig.forEach((sec) => {
    if (sections[sec.key].enabled) {
      allRemovedFileIds = [...allRemovedFileIds, ...sections[sec.key].removedFileIds];
    }
  });

  const payload: GenerateMonthlyReportForm = {
    client_id: form.client_id,
    month_number: form.month_number,
    report_month_label: form.report_month_label,
    title: form.title,
    date_from: form.date_from,
    date_to: form.date_to,
    selected_sections: selected,
    report_data_json: JSON.stringify(reportData),
    publish: form.publish,
    status: form.status,
    sections: payloadSections,
    removedFileIds: allRemovedFileIds,
  };

  const uploadFiles: Record<string, File[]> = {};

  if (branding.logoFile) {
    uploadFiles["logo"] = [branding.logoFile];
  }
  if (branding.heroImageFile) {
    uploadFiles["hero_image"] = [branding.heroImageFile];
  }

  reportSectionConfig.forEach((sec) => {
    const s = sections[sec.key];
    if (s.enabled && s.newFiles.length > 0) {
      uploadFiles[`sections[${sec.key}][files]`] = s.newFiles;
    }
  });

  const extractedPayload: Record<string, string> = {};
  reportSectionConfig.forEach((sec) => {
    const contents = sectionExtractedContents[sec.key];
    if (sections[sec.key].enabled && contents && contents.length > 0) {
      extractedPayload[`sections[${sec.key}][extracted_contents]`] = JSON.stringify(contents);
    }
  });

  return { payload, files: uploadFiles, extractedPayload };
}

function submit(submitAction: "draft" | "html" | "pdf" | "generate") {
  if (!validateReportForm()) {
    const firstError = document.querySelector("[class*='text-\\[#be243d\\]']");
    firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
    return;
  }

  form.publish = submitAction === "generate" || submitAction === "pdf";
  form.status = submitAction === "draft" ? "draft" : "generated";

  const { payload, files, extractedPayload } = serializeReportPayload();

  emit("generate", {
    payload,
    files,
    action: submitAction,
    reportId: props.report?.id || null,
    extractedPayload,
  });
}

function emitClose() {
  emit("update:open", false);
}

watch(
  () => [props.open, props.selectedClient, props.selectedMonthNumber, props.report],
  ([open]) => {
    if (open) {
      setDefaults();
      fetchAvailableModels();
    }
  },
  { immediate: true, deep: true }
);
</script>

<style scoped>
.field-input {
  width: 100%;
  border-radius: 0.95rem;
  border: 1px solid #d3dce9;
  background: white;
  padding: 0.75rem 0.9rem;
  font-size: 0.94rem;
  color: #243049;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.field-input:focus {
  border-color: #8eb4ff;
  box-shadow: 0 0 0 2px #cfe0ff;
}

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.pill-blue {
  background: #eaf3fb;
  color: #2855a6;
}
.pill-yellow {
  background: #fff6d8;
  color: #8b6b00;
}
.pill-red {
  background: #ffe9ec;
  color: #be243d;
}
.pill-green {
  background: #ecfdf5;
  color: #15803d;
}

.modal-body-scroll {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
.modal-body-scroll::-webkit-scrollbar {
  width: 6px;
}
.modal-body-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.modal-body-scroll::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 9999px;
}
.modal-body-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}
</style>

<style>
/* Suppress duplicate default reka-ui DialogClose button */
.report-builder-dialog-content > button.absolute,
.report-builder-dialog-content > button:has(.sr-only) {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
}
</style>
